import { useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "@/lib/api-fetch";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const res = await apiFetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Something went wrong");
      } else {
        setSubmitted(true);
      }
    } catch {
      setError("Can't reach the server. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 pt-16">
      <div className="w-full max-w-md">
        <div className="rounded-xl border border-border bg-card p-8 shadow-lg">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 text-foreground hover:opacity-90">
              <img src="/logo.png" alt="CompostDeveloper" className="h-10 w-10 rounded" />
              <span className="text-xl font-bold">
                Compost<span className="text-forest-500">Developer</span>
              </span>
            </Link>
            <h1 className="mt-6 text-2xl font-semibold text-foreground">Reset password</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {submitted
                ? "Check your email for a reset link."
                : "Enter your email and we'll send you a reset link."}
            </p>
          </div>

          {submitted ? (
            <div className="space-y-4">
              <div className="rounded-lg bg-forest-500/10 border border-forest-500/20 px-4 py-3 text-sm text-forest-400">
                If an account exists for <strong>{email}</strong>, you'll receive a password reset email shortly.
              </div>
              <p className="text-center text-sm text-muted-foreground">
                <Link to="/login" className="text-forest-500 hover:text-forest-400 font-medium">
                  Back to sign in
                </Link>
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="rounded-lg bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-400">
                  {error}
                </div>
              )}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1.5">
                  Email address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 bg-secondary border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-forest-600 focus:border-transparent text-sm"
                  placeholder="you@facility.com"
                  required
                  autoFocus
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 px-4 bg-forest-600 hover:bg-forest-700 disabled:opacity-50 text-white font-medium rounded-lg transition-colors"
              >
                {submitting ? "Sending…" : "Send reset link"}
              </button>
              <p className="text-center text-sm text-muted-foreground">
                Remember your password?{" "}
                <Link to="/login" className="text-forest-500 hover:text-forest-400 font-medium">
                  Sign in
                </Link>
              </p>
            </form>
          )}
        </div>
        <p className="mt-6 text-center">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">
            ← Back to home
          </Link>
        </p>
      </div>
    </div>
  );
}
